import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'
import { buildSalesLeadParams, type SalesLeadFilters } from './salesLead'
import type { SequenceStepInput } from './sequence'

const BASE = '/api/v1/insure/internal/outbound-call-automations'
const FILTER_PREVIEW = '/api/v1/insure/internal/sales-leads/filter-preview/'
const KEY = ['outbound-call-automation']

/**
 * Sales-leads query param that opens the automation dialog pre-composed as a
 * follow-up of that run (INS-290). Mirrors `AUTOMATION_FOLLOW_UP_PARAM` on the
 * backend, which builds the same link for the reminder email's CTA.
 */
export const AUTOMATION_FOLLOW_UP_PARAM = 'automation_follow_up'

export function automationFollowUpUrl(automationId: string): string {
  return `/insure/internal/sales-leads?${AUTOMATION_FOLLOW_UP_PARAM}=${automationId}`
}

export enum AutomationStatus {
  NOT_STARTED = 'NOT_STARTED',
  RUNNING = 'RUNNING',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
}

export const AUTOMATION_STATUS_LABELS: Record<AutomationStatus, string> = {
  [AutomationStatus.NOT_STARTED]: 'Not started',
  [AutomationStatus.RUNNING]: 'Running',
  [AutomationStatus.PAUSED]: 'Paused',
  [AutomationStatus.COMPLETED]: 'Completed',
}

export interface AutomationLeadCounts {
  total: number
  notStarted: number
  processing: number
  /** Terminal members, failed dials included — the set the "done" tag filters to. */
  done: number
  /** Leads a call actually reached the network for: `done` minus `failed`. */
  called: number
  // Terminal-outcome breakdown (all within `done`), plus skipped leads.
  transferred: number
  noAnswer: number
  /** Never dialed at all — no phone, or Phonic refused/timed out. Not a no-answer. */
  failed: number
  skipped: number
}

// SalesLeadTag id per status, so a dialog card can filter the leads list to this
// automation's pending/processing/done/skipped leads.
export interface AutomationStatusTags {
  notStarted: string | null
  processing: string | null
  done: string | null
  skipped: string | null
}

/** A scheduled "run a follow-up of this run" reminder (INS-290). */
export interface AutomationFollowUpReminder {
  id: string
  reminderDate: string
  message: string
}

export interface OutboundCallAutomation {
  id: string
  name: string
  status: AutomationStatus
  limit: number | null
  leadFilters: Record<string, string>
  sequenceTemplate: string | null
  // Auto-pause config: pause `pauseDurationSeconds` after every `pauseAfterLeads`
  // calls. Both null = no auto-pause. `autoPausedUntil` is set (ISO) while a pause
  // is in effect, else null.
  pauseAfterLeads: number | null
  pauseDurationSeconds: number | null
  autoPausedUntil: string | null
  // Follow-ups (INS-253): the SalesLead status this run dials (COLD normally,
  // CALLED for a follow-up); the run this was a follow-up of; and how many of this
  // run's leads the calls didn't reach (no answer or a failed dial).
  targetStatus: string
  followUpOf: string | null
  followUpEligibleCount: number
  /** The owner's scheduled reminder to run a follow-up of this run, or null. */
  followUpReminder: AutomationFollowUpReminder | null
  leadCounts: AutomationLeadCounts
  filterSummary: string[]
  statusTags: AutomationStatusTags
  createdAt: string
  updatedAt: string
}

export interface CreateAutomationInput {
  name: string
  leadFilters: Record<string, string>
  limit: number | null
  pauseAfterLeads: number | null
  pauseDurationSeconds: number | null
  /** Source run id when this is a follow-up (re-dial the leads it didn't reach), else null. */
  followUpOf: string | null
  sequenceSteps: SequenceStepInput[]
  start: boolean
}

export interface FilterPreview {
  count: number
  summary: string[]
}

/** Live count + human-readable summary for the current lead filters. */
export function useFilterPreview(filters: SalesLeadFilters, enabled: boolean) {
  const params = buildSalesLeadParams(filters)
  return useQuery({
    queryKey: ['filter-preview', params],
    queryFn: () => axios.get(FILTER_PREVIEW, { params }).then((r) => toCamelCase(r.data) as FilterPreview),
    enabled,
  })
}

/**
 * One run by id, scoped to the owner server-side (404 otherwise). Used to resolve a
 * reminder's deep link — the history list is paginated, so an older run wouldn't be
 * in it. Disabled while `id` is null.
 */
export function useAutomation(id: string | null) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => axios.get(`${BASE}/${id}/`).then((r) => toCamelCase(r.data) as OutboundCallAutomation),
    enabled: !!id,
    retry: false, // a 404 (someone else's run, or deleted) is an answer, not a blip
  })
}

/** Completed past runs (newest first), for the run-history list. */
export function usePastAutomations() {
  return useQuery({
    queryKey: [...KEY, 'past'],
    queryFn: () =>
      axios
        .get(`${BASE}/`, { params: { status: AutomationStatus.COMPLETED } })
        .then((r) => toCamelCase(r.data.results ?? []) as OutboundCallAutomation[]),
  })
}

/** The rep's current automation (running/paused, or completed-but-undismissed), or null. */
export function useActiveAutomation() {
  return useQuery({
    queryKey: [...KEY, 'active'],
    queryFn: () =>
      axios
        .get(`${BASE}/active/`)
        .then((r) => (r.data ? (toCamelCase(r.data) as OutboundCallAutomation) : null)),
  })
}

export function useCreateAutomation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateAutomationInput) =>
      axios.post(`${BASE}/`, toSnakeCase(input)).then((r) => toCamelCase(r.data) as OutboundCallAutomation),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

function useLifecycleAction(action: 'start' | 'pause' | 'cancel' | 'dismiss') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios.post(`${BASE}/${id}/${action}/`).then((r) => toCamelCase(r.data) as OutboundCallAutomation),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

interface SetFollowUpReminderInput {
  id: string
  reminderDate: string
  note?: string
}

const FOLLOW_UP_REMINDER_KEYS = [KEY, [CacheKeys.MY_REMINDERS]]

function invalidateAutomationsAndReminders(qc: ReturnType<typeof useQueryClient>) {
  for (const queryKey of FOLLOW_UP_REMINDER_KEYS) qc.invalidateQueries({ queryKey })
}

/** Schedule (or reschedule) a reminder to run a follow-up of a past run (INS-290). */
export function useSetAutomationFollowUpReminder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reminderDate, note }: SetFollowUpReminderInput) =>
      axios
        .put(
          `${BASE}/${id}/follow-up-reminder/`,
          toSnakeCase({
            reminderDate,
            note: note ?? '',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          })
        )
        .then((r) => toCamelCase(r.data) as OutboundCallAutomation),
    onSuccess: () => invalidateAutomationsAndReminders(qc),
  })
}

export function useClearAutomationFollowUpReminder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios
        .delete(`${BASE}/${id}/follow-up-reminder/`)
        .then((r) => toCamelCase(r.data) as OutboundCallAutomation),
    onSuccess: () => invalidateAutomationsAndReminders(qc),
  })
}

export const useStartAutomation = () => useLifecycleAction('start')
export const usePauseAutomation = () => useLifecycleAction('pause')
export const useCancelAutomation = () => useLifecycleAction('cancel')
export const useDismissAutomation = () => useLifecycleAction('dismiss')
